import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setEmailData, setPasswordData, clearError } from '@/features/auth/authSlice';
import { businessSignIn, getBusinessAuthMessage } from '@/features/auth';
import { selectActiveInvitation, selectActiveInvitationToken } from '@/features/invitations';
import { EmailLoginForm } from '@/components/email-login-form';

const hasMinimumPasswordCharacters = (value: string): boolean => value.replace(/\s/g, '').length >= 3;

export default function LoginScreen() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { email, password, isLoading, error, isAuthenticated } = useAppSelector((s) => s.auth);
  const activeInvitation = useAppSelector(selectActiveInvitation);
  const activeInvitationToken = useAppSelector(selectActiveInvitationToken);
  const defaultRedirectTarget = (location.state as { from?: string } | null)?.from || '/';
  const redirectTarget = activeInvitationToken ? '/invite' : defaultRedirectTarget;
  const canSubmit = Boolean(email?.trim()) && hasMinimumPasswordCharacters(password);
  const registerState = {
    from: redirectTarget,
    email: activeInvitation?.email || email || undefined,
    organizationName:
      activeInvitation?.orgName || (location.state as { organizationName?: string } | null)?.organizationName,
  };

  // Pre-fill email from the active invitation or route state.
  useEffect(() => {
    const locationState = location.state as { email?: string; organizationName?: string; from?: string } | null;
    const nextEmail = activeInvitation?.email || locationState?.email;
    if (nextEmail && nextEmail !== email) {
      dispatch(setEmailData(nextEmail));
    }
  }, [activeInvitation?.email, dispatch, email, location.state]);

  // Redirect on authentication
  useEffect(() => {
    if (isAuthenticated) navigate(redirectTarget, { replace: true });
  }, [isAuthenticated, navigate, redirectTarget]);

  const onEmailChange = (val: string) => {
    dispatch(setEmailData(val));
    dispatch(clearError());
  };

  const onPasswordChange = (val: string) => {
    dispatch(setPasswordData(val));
    dispatch(clearError());
  };

  const onSubmitPassword = () => {
    if (!canSubmit || !email) return;

    const signInPromise = dispatch(businessSignIn({ email, password })).unwrap();

    toast.promise(signInPromise, {
      loading: 'Signing in...',
      success: 'Signed in successfully.',
      error: (err) => getBusinessAuthMessage(err?.message, 'Login failed'),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Login to your account</h1>
        <p className="text-muted-foreground text-sm text-balance">Enter your credentials to continue</p>
      </div>
      <EmailLoginForm
        email={email ?? ''}
        password={password}
        canSubmit={canSubmit}
        isLoading={isLoading}
        hasError={!!error}
        errorMessage={error}
        registerState={registerState}
        onEmailChange={onEmailChange}
        onPasswordChange={onPasswordChange}
        onSubmitPassword={onSubmitPassword}
      />
    </div>
  );
}
